import React, { useState } from 'react';
import { 
  LifeBuoy, 
  Search, 
  CheckCircle2, 
  Clock, 
  User, 
  MessageSquare, 
  AlertCircle, 
  Send,
  UserCheck
} from 'lucide-react';
import { StatusBadge } from '../components/common/StatusBadge';
import { Modal } from '../components/common/Modal';
import { SupportTicket } from '../types/admin';
import { useAuth } from '../context/AuthContext';

export const SupportTicketsView: React.FC = () => {
  const { currentUser } = useAuth();
  const [tickets, setTickets] = useState<SupportTicket[]>([
    {
      id: 'tkt_001',
      ticketNumber: 'TCK-4812',
      userId: 'usr_003_chioma',
      userName: 'Chioma Eze',
      userEmail: 'chioma.eze@hotmail.com',
      category: 'payment',
      priority: 'high',
      status: 'open',
      subject: 'Wallet funding via Flutterwave bank transfer pending for 45 mins',
      message: 'I initiated a bank transfer of ₦35,000 using the Flutterwave checkout. My bank debited my account, but the funds have not reflected on my ReUseIt wallet balance yet.',
      createdAt: '45 mins ago',
      updatedAt: '45 mins ago',
      userReplies: [
        {
          id: 'rep_1',
          sender: 'user',
          senderName: 'Chioma Eze',
          message: 'My transaction reference number was FLW-TX-9842-chioma.',
          timestamp: '40 mins ago'
        }
      ]
    },
    {
      id: 'tkt_002',
      ticketNumber: 'TCK-4809',
      userId: 'usr_002_tunde',
      userName: 'Tunde Bakare',
      userEmail: 'tunde.bakare@yahoo.com',
      category: 'escrow',
      priority: 'medium',
      status: 'in_progress',
      subject: 'Clarification on 48-hour auto-settlement countdown',
      message: 'Hello, the buyer received my teak chair yesterday morning. Does the 48-hr auto-settlement calculate from delivery confirmation or rider dispatch time?',
      assignedAgent: 'Sammy Oliver (Super Admin)',
      agentNotes: 'Explained to vendor that 48-hr timer begins exactly when buyer marks Delivered or courier API confirms drop-off.',
      createdAt: 'Yesterday at 05:20 PM',
      updatedAt: '2 hours ago',
      userReplies: []
    },
    {
      id: 'tkt_003',
      ticketNumber: 'TCK-4780',
      userId: 'usr_004_fatima',
      userName: 'Fatima Sani',
      userEmail: 'fatima.s@outlook.com',
      category: 'account',
      priority: 'low',
      status: 'resolved',
      subject: 'How to update phone number linked to my profile',
      message: 'I changed my SIM card and want to update my WhatsApp phone number for courier dispatches.',
      assignedAgent: 'Support Staff #3',
      createdAt: '3 days ago',
      updatedAt: 'Yesterday',
      userReplies: [
        {
          id: 'rep_2',
          sender: 'agent',
          senderName: 'Support Desk',
          message: 'Hello Fatima, you can update your phone directly under Settings -> Edit Profile in the ReUseIt mobile app.',
          timestamp: 'Yesterday'
        }
      ]
    }
  ]);

  const [activeTicket, setActiveTicket] = useState<SupportTicket | null>(null);
  const [replyText, setReplyText] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const handleSendReply = () => {
    if (!activeTicket || !replyText.trim()) return;
    const newReply = {
      id: `rep_${Date.now()}`,
      sender: 'agent' as const,
      senderName: currentUser?.displayName || 'Support Staff',
      senderAvatar: currentUser?.avatarUrl,
      senderRole: currentUser?.role || 'support_agent',
      message: replyText,
      timestamp: 'Just now'
    };

    const updated = {
      ...activeTicket,
      assignedAgent: currentUser?.displayName || activeTicket.assignedAgent || 'Support Agent',
      userReplies: [...(activeTicket.userReplies || []), newReply],
      status: 'in_progress' as const,
      updatedAt: 'Just now'
    };

    setTickets(prev => prev.map(t => t.id === activeTicket.id ? updated : t));
    setActiveTicket(updated);
    setReplyText('');
  };

  const handleStatusChange = (status: SupportTicket['status']) => {
    if (!activeTicket) return;
    const updated = { ...activeTicket, status, updatedAt: 'Just now' };
    setTickets(prev => prev.map(t => t.id === activeTicket.id ? updated : t));
    setActiveTicket(updated);
  };

  const handleSaveInternalNote = () => {
    if (!activeTicket || !internalNote.trim()) return;
    const updated = { ...activeTicket, agentNotes: internalNote };
    setTickets(prev => prev.map(t => t.id === activeTicket.id ? updated : t));
    setActiveTicket(updated);
    setInternalNote('');
  };

  const filtered = tickets.filter(t => 
    t.ticketNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.subject.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] pb-4">
        <div>
          <h3 className="text-lg font-bold text-white dark:text-white light:text-[#0D1C17]">
            Customer Support Ticket Helpdesk
          </h3>
          <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
            Triage user inquiries, wallet delays, and courier questions with direct in-app messaging delivery
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold px-3 py-1 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/30">
            {tickets.filter(t => t.status === 'open').length} Open Tickets
          </span>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="p-4 rounded-2xl border transition-colors flex items-center justify-between gap-4
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
        light:bg-white light:border-[#D5E4DC]
      ">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tickets by ID, user, or topic..."
            className="w-full pl-9 pr-4 py-2 rounded-xl text-xs outline-none border transition-colors
              bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
              light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
              focus:border-brand-500"
          />
        </div>
      </div>

      {/* Ticket List */}
      <div className="rounded-3xl border overflow-hidden transition-colors
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
        light:bg-white light:border-[#D5E4DC] light:shadow-sm
      ">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase tracking-wider font-semibold">
                <th className="py-4 px-6">Ticket #</th>
                <th className="py-4 px-6">User</th>
                <th className="py-4 px-6">Category & Subject</th>
                <th className="py-4 px-6">Priority</th>
                <th className="py-4 px-6">Assigned Agent</th>
                <th className="py-4 px-6">Status</th>
                <th className="py-4 px-6 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#22493c] dark:divide-[#22493c] light:divide-[#EBF2EE]">
              {filtered.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-[#1a382e]/50 dark:hover:bg-[#1a382e]/50 light:hover:bg-gray-50/80 transition-colors">
                  <td className="py-4 px-6 font-mono font-bold text-brand-400">
                    {ticket.ticketNumber}
                  </td>
                  <td className="py-4 px-6">
                    <div className="font-bold text-white dark:text-white light:text-[#0D1C17]">{ticket.userName}</div>
                    <div className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">{ticket.userEmail}</div>
                  </td>
                  <td className="py-4 px-6 max-w-sm">
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#10231d] dark:bg-[#10231d] text-brand-400 mr-2">
                      {ticket.category}
                    </span>
                    <span className="font-medium text-white dark:text-white light:text-[#0D1C17]">
                      {ticket.subject}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <StatusBadge status={ticket.priority} />
                  </td>
                  <td className="py-4 px-6 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                    {ticket.assignedAgent || 'Unassigned'}
                  </td>
                  <td className="py-4 px-6">
                    <StatusBadge status={ticket.status} />
                  </td>
                  <td className="py-4 px-6 text-right">
                    <button
                      onClick={() => setActiveTicket(ticket)}
                      className="px-3 py-1.5 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547] transition-colors"
                    >
                      Manage Ticket
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ticket Details & Reply Modal */}
      {activeTicket && (
        <Modal
          isOpen={Boolean(activeTicket)}
          onClose={() => setActiveTicket(null)}
          title={`Ticket ${activeTicket.ticketNumber}: ${activeTicket.subject}`}
          subtitle={`Raised by ${activeTicket.userName} (${activeTicket.userEmail}) • Priority: ${activeTicket.priority.toUpperCase()}`}
          maxWidth="2xl"
        >
          <div className="space-y-6 text-xs">
            {/* User message */}
            <div className="p-4 rounded-2xl bg-[#10231d] dark:bg-[#10231d] light:bg-gray-50 border border-[#22493c] dark:border-[#22493c] space-y-2">
              <span className="text-[10px] uppercase font-bold text-brand-400">User Inbound Message:</span>
              <p className="text-white dark:text-white light:text-gray-900 leading-relaxed">
                {activeTicket.message}
              </p>
            </div>

            {/* Conversation Thread */}
            {activeTicket.userReplies && activeTicket.userReplies.length > 0 && (
              <div className="space-y-2.5">
                <h5 className="font-bold text-white dark:text-white light:text-gray-900">Communication History</h5>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {activeTicket.userReplies.map((r) => (
                    <div 
                      key={r.id} 
                      className={`p-3.5 rounded-2xl ${
                        r.sender === 'agent' 
                          ? 'bg-brand-500/10 border border-brand-500/25 text-[#f5f5f0] ml-6 shadow-glow-sm' 
                          : 'bg-[#22493c] text-white mr-6'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {r.sender === 'agent' ? (
                            <div className="w-6 h-6 rounded-full overflow-hidden border border-brand-500/40 shrink-0 bg-brand-500/20 flex items-center justify-center text-[10px] text-brand-400 font-bold">
                              {r.senderAvatar ? (
                                <img src={r.senderAvatar} alt={r.senderName} className="w-full h-full object-cover" />
                              ) : (
                                r.senderName.charAt(0)
                              )}
                            </div>
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 font-bold text-[10px] flex items-center justify-center shrink-0">
                              {r.senderName.charAt(0)}
                            </div>
                          )}
                          <span className="font-bold text-xs text-white">{r.senderName}</span>
                          <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded uppercase tracking-wider
                            ${r.sender === 'agent' 
                              ? 'bg-brand-500/20 text-brand-400 border border-brand-500/30' 
                              : 'bg-emerald-500/20 text-emerald-300'
                            }`}
                          >
                            {r.sender === 'agent' ? (r.senderRole?.replace('_', ' ') || 'Staff') : 'User'}
                          </span>
                        </div>
                        <span className="text-[10px] text-[#90cbb7]/70 font-medium">{r.timestamp}</span>
                      </div>
                      <p className="pl-8 text-xs leading-relaxed text-[#f5f5f0] dark:text-[#f5f5f0] light:text-gray-800">
                        {r.message}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reply Input */}
            <div className="space-y-2">
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                Direct Agent Reply (Sent to user device):
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type support response to send..."
                  className="flex-1 px-4 py-2.5 rounded-xl text-xs outline-none border transition-colors
                    bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                    light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                    focus:border-brand-500"
                />
                <button
                  type="button"
                  onClick={handleSendReply}
                  disabled={!replyText.trim()}
                  className="px-4 py-2.5 rounded-xl font-bold text-forest-void bg-brand-500 hover:bg-brand-400 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Reply</span>
                </button>
              </div>
            </div>

            {/* Internal Notes */}
            <div className="space-y-2 pt-3 border-t border-[#22493c] dark:border-[#22493c]">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">Internal Agent Rationale & Notes:</span>
                {activeTicket.agentNotes && <span className="text-[10px] text-amber-400 font-semibold">Notes saved</span>}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  placeholder={activeTicket.agentNotes || "Add confidential internal note for fellow mediators..."}
                  className="flex-1 px-4 py-2 rounded-xl text-xs outline-none border transition-colors
                    bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                    light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]"
                />
                <button
                  type="button"
                  onClick={handleSaveInternalNote}
                  disabled={!internalNote.trim()}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547] disabled:opacity-50"
                >
                  Save Note
                </button>
              </div>
            </div>

            {/* Change Status Footer */}
            <div className="pt-4 border-t border-[#22493c] dark:border-[#22493c] flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#90cbb7] light:text-gray-500">Status:</span>
                {(['open', 'in_progress', 'resolved', 'closed'] as const).map((st) => (
                  <button
                    key={st}
                    onClick={() => handleStatusChange(st)}
                    className={`px-3 py-1 rounded-full text-[11px] font-bold capitalize transition-all ${activeTicket.status === st ? 'bg-brand-500 text-forest-void' : 'bg-[#22493c] text-[#90cbb7] hover:text-white'}`}
                  >
                    {st.replace('_', ' ')}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setActiveTicket(null)}
                className="px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547]"
              >
                Done
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
